import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';

import {useCallback, useEffect, useEffectEvent, useRef} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isEmpty} from 'ramda';

import {
  isTerminalAvailabilityStatus,
  patchVehicleAvailabilityInCaches,
  sourcingApi,
} from '@dms/api/core';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {
  AVAILABILITY_CHECK_MAX_AD_IDS,
  AVAILABILITY_CHECK_MAX_DURATION_MS,
  AVAILABILITY_CHECK_POLLING_INTERVAL_MS,
} from '../constants/sourcingAvailabilityCheck';
import {useSourcingIntervalPoller} from './useSourcingIntervalPoller';

type SourcingAvailabilityApiState = Parameters<
  ReturnType<typeof sourcingApi.endpoints.sourcingAvailabilityByAdId.select>
>[0];

type UseSourcingListAvailabilityProps = {
  adIds: string[];
  enabled: boolean;
  /**
   * Bump on user-driven loads (filter/order change, infinite-scroll page, user-list fetch).
   * Resets the 2 min give-up window. Do not bump on real-time prepend alone.
   */
  userLoadKey: string | number;
};

function getUnresolvedAdIds(state: SourcingAvailabilityApiState, adIds: string[]): string[] {
  const unresolved: string[] = [];

  for (const adId of adIds) {
    if (unresolved.length >= AVAILABILITY_CHECK_MAX_AD_IDS) {
      break;
    }

    const cached = sourcingApi.endpoints.sourcingAvailabilityByAdId.select({adId})(state);
    // Terminal ByAdId (active / just_sold) → already checked: skip re-request.
    // List grey-out / Sold / hide-actions come from justSold patched onto list row caches.
    if (!isTerminalAvailabilityStatus(cached.data?.status)) {
      unresolved.push(adId);
    }
  }

  return unresolved;
}

/**
 * Batch-polls `/availability-check` for loaded classifieds list rows.
 * Side-effect only — list UI relies on `justSold` cache patches (no checking Flags).
 *
 * Once an adId has a terminal `sourcingAvailabilityByAdId` entry, it is not checked again
 * in this session (until cache GC). That is intentional: listing already greys out via
 * the `justSold` patch from the first terminal result.
 */
export function useSourcingListAvailability(props: UseSourcingListAvailabilityProps) {
  const dispatch =
    useDispatch<ThunkDispatch<SourcingAvailabilityApiState, unknown, UnknownAction>>();
  const store = useStore<SourcingAvailabilityApiState>();

  const adIdsRef = useRef(props.adIds);
  adIdsRef.current = props.adIds;

  const deadlineMsRef = useRef(0);
  const stoppedRef = useRef(false);
  const inFlightRef = useRef(false);

  const syncJustSoldOntoListCaches = useEffectEvent(() => {
    const state = store.getState();

    for (const adId of adIdsRef.current) {
      const status = sourcingApi.endpoints.sourcingAvailabilityByAdId.select({adId})(state).data
        ?.status;
      if (!isTerminalAvailabilityStatus(status)) {
        continue;
      }

      // Server list payloads reset justSold to null; re-apply from durable ByAdId cache.
      dispatch((innerDispatch, getState) => {
        patchVehicleAvailabilityInCaches(adId, status, {
          dispatch: innerDispatch,
          getState,
        });
      });
    }
  });

  // useCallback (not useEffectEvent): passed into useSourcingIntervalPoller, which wraps it in
  // useEffectEvent internally. Effect Events must not leave the component that created them.
  const doPoll = useCallback(async () => {
    if (stoppedRef.current || inFlightRef.current || document.hidden) {
      return;
    }
    if (Date.now() >= deadlineMsRef.current) {
      return;
    }

    const unresolvedAdIds = getUnresolvedAdIds(store.getState(), adIdsRef.current);
    if (isEmpty(unresolvedAdIds)) {
      return;
    }

    inFlightRef.current = true;
    const subscription = dispatch(
      sourcingApi.endpoints.sourcingAvailabilityCheck.initiate(
        {adIds: unresolvedAdIds},
        {forceRefetch: true}
      )
    );

    try {
      await subscription.unwrap().catch((error: ApiError) => {
        handleApiError(error);
        stoppedRef.current = true;
      });
    } finally {
      subscription.unsubscribe();
      inFlightRef.current = false;
    }
  }, [dispatch, store]);

  // Re-hydrate justSold after list refetch / membership change (API returns justSold: null).
  // useEffect: sync after cache presence changes. No approved-pattern equivalent.
  useEffect(
    function syncJustSoldWhenListChanges() {
      if (!props.enabled) {
        return;
      }

      syncJustSoldOntoListCaches();
    },
    // eslint-disable-next-line react/exhaustive-deps -- join tracks membership; array identity is unstable.
    [props.enabled, props.userLoadKey, props.adIds.join(',')]
  );

  useSourcingIntervalPoller({
    enabled: props.enabled,
    pollingInterval: AVAILABILITY_CHECK_POLLING_INTERVAL_MS,
    resetKey: props.userLoadKey,
    tickKey: props.adIds.join(','),
    onRestart: () => {
      stoppedRef.current = false;
      deadlineMsRef.current = Date.now() + AVAILABILITY_CHECK_MAX_DURATION_MS;
    },
    onTick: () => {
      // Prepend / membership growth must retry even after a prior API error stopped polling.
      stoppedRef.current = false;
    },
    doPoll,
  });
}
